import { isSdkModelIsAttachment } from '@src/slide/domain/ui-model/elements/element-judge';
import { DataType, IChartData, IDrawingShapeData, IGroupShapeDataModel, ISlideData, ISlideDataModel, ITimingData, UnitTimeNode } from '@tencent/slide-sdk-model';
import omit from 'lodash/omit';
import { cloneDeep } from '@tencent/slide-sdk-model/src/utils/clone-deep';

export default class SdkAttachmentElementFilter {
    /**
     * 过滤掉附件类型的数据
     * @param sdkDataArray
     * @returns
     */
    public static filter(sdkDataArray: any[]): [any[], boolean] {
        let filteredFlag = false;

        // 判断当前 model 是否是附件，会修改标记
        const checkIsAttachment = (data) => {
            const res = isSdkModelIsAttachment(data);
            if (res) {
                filteredFlag = true;
            }
            return res;
        };

        // 过滤所有附件元素
        const filteredSdkData = sdkDataArray.reduce((result, sdkDataItem) => {
            const isSlide = sdkDataItem.type === DataType.Slide;
            if (isSlide) {
                result.push(this.filterAttachmentInSlide(sdkDataItem, checkIsAttachment));
                return result;
            }
            const newItem = this.filterAttachmentInChildData(sdkDataItem, checkIsAttachment);
            return this.addIfTruly(newItem, result);
        }, []);

        return [filteredSdkData, filteredFlag];
    }

    /**
     * 过滤 slide sdk 数据中的附件元素
     * @param sdkSlideData
     * @param checkIsAttachment
     * @returns
     */
    private static filterAttachmentInSlide(sdkSlideData, checkIsAttachment): ISlideData {
        const slideDataModel = sdkSlideData as ISlideDataModel;
        const slideData: ISlideData = slideDataModel.getWritableOwnedData();
        // 过滤附件元素
        const shapeList: IDrawingShapeData[] = slideDataModel.shapeList.reduce((result, item) => {
            const newItem = this.filterAttachmentInChildData(item, checkIsAttachment);
            return this.addIfTruly(newItem, result);
        }, []);
        // 过滤附件动画
        const deletedShapeIds = slideDataModel.shapeList
            .filter(shape => !shapeList.some(s => s.id === shape.id))
            .map(shape => shape.id);
        const timing = slideData.timing && this.filterAttachmentTiming(deletedShapeIds, slideData.timing);
        return {
            // cSld 数据量大，深拷贝耗时
            ...(cloneDeep(omit(slideData, ['cSld', 'timing']))),
            cSld: {
                ...slideData.cSld,
                ...(slideData.cSld.spTree ? {
                    spTree: {
                        ...slideData.cSld.spTree,
                        shapeList,
                    } } : {}),
            },
            timing,
        };
    }

    /**
     * 过滤掉附件元素后过滤动画：删除绑定在附件上的动画
     * 过滤后的动画数据进行浅拷贝，拷贝至 unitTimeNodeList 这一层
     * @param deletedShapeIds 删除附件的 ID
     * @param timing 原始动画数据
     * @returns 过滤后浅拷贝的动画数据
     */
    private static filterAttachmentTiming(deletedShapeIds: string[], timing: ITimingData): ITimingData {
        const newTiming: ITimingData = { ...timing };
        if (!timing.unitTimeNodeMap) {
            return newTiming;
        }
        newTiming.unitTimeNodeMap = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, unitTimeNode] of Object.entries(timing.unitTimeNodeMap)) {
            if (!unitTimeNode?.unitTimeNodeList || deletedShapeIds.includes(key)) {
                continue;
            }
            newTiming.unitTimeNodeMap[key] = {
                ...unitTimeNode,
                unitTimeNodeList: unitTimeNode.unitTimeNodeList.filter(timeNode => !deletedShapeIds.some(id => id === UnitTimeNode.getShapeId(timeNode) ?? '')),
            };
        }
        return newTiming;
    }

    /**
     * 过滤掉子级 sdkData 中的附件元素
     * 子级 sdkData 定义为 slide 的子级元素
     * @param childData
     * @param checkIsAttachment
     * @returns
     */
    private static filterAttachmentInChildData(childData, checkIsAttachment) {
        const isGroupShape = childData.type === DataType.GroupShape;
        if (isGroupShape) {
            return this.filterAttachmentInGroup(childData, checkIsAttachment);
        }
        return this.filterAttachmentInNormal(childData, checkIsAttachment);
    }

    /**
     * 过滤组合中的附件元素
     * @param sdkGroupData
     * @param checkIsAttachment
     * @returns
     */
    private static filterAttachmentInGroup(sdkGroupData: IGroupShapeDataModel, checkIsAttachment) {
        const shapeList = [];
        sdkGroupData.shapeList.forEach((item) => {
            // 组合中可能会嵌套组合
            const newItem = this.filterAttachmentInChildData(item, checkIsAttachment);
            this.addIfTruly(newItem, shapeList);
        });

        // 组合中没元素，直接删除
        if (shapeList.length === 0) {
            return null;
        }
        // 组合中只有一个元素，取消组合
        if (shapeList.length === 1) {
            return shapeList[0];
        }
        return {
            ...((sdkGroupData as IGroupShapeDataModel)?.getWritableOwnedData()),
            shapeList,
        };
    }

    /**
     * 过滤附件元素
     * @param sdkData
     * @param checkIsAttachment
     * @returns
     */
    private static filterAttachmentInNormal(sdkData, checkIsAttachment) {
        if (checkIsAttachment(sdkData)) {
            return null;
        }
        if (!sdkData?.getWritableOwnedData()) {
            return undefined;
        }
        // 图表需要额外浅拷贝 subChart
        if (sdkData.type === DataType.GraphicalObjectFrameChart) {
            const data = sdkData.getWritableOwnedData() as IChartData;
            return {
                ...data,
                chartSpace: data.chartSpace && {
                    ...data.chartSpace,
                    chart: data.chartSpace.chart && {
                        ...data.chartSpace.chart,
                        plotArea: data.chartSpace.chart.plotArea && {
                            ...data.chartSpace.chart.plotArea,
                            charts: data.chartSpace?.chart?.plotArea?.charts?.map(i => i),
                        },
                    },
                },
            };
        }
        return sdkData?.getWritableOwnedData();
    }

    /**
     * 如果当前值是真值，添加并返回完整数据
     * @param value
     * @param array
     * @returns
     */
    private static addIfTruly(value: any, array: any[]) {
        if (value) {
            array.push(value);
        }
        return array;
    }
}
